import {
  column,
  Css,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridRowLookup,
  GridTableLayout,
  multiFilter,
  PageHeaderLayout,
  RowStyles,
  simpleHeader,
  SimpleHeaderAndData,
  SuperDrawerWidth,
  useGridTableLayoutState,
  useSuperDrawer,
} from "@homebound/beam";
import { MutableRefObject, useMemo, useRef } from "react";
import { chipCell } from "src/components";
import { useRoomTypeFilter } from "src/components/autoPopulateFilters/useRoomTypeFilter";
import { LocationsFilter, LocationType } from "src/generated/graphqlTypes";
import { LocationDrawer } from "src/routes/admin/locations/components/LocationDrawer";
import {
  AdminLocationsPage_LocationFragment,
  AdminLocationsPageQuery,
  useAdminLocationsPageQuery,
} from "src/routes/admin/locations/LocationsPage.generated";
import { safeEntries } from "src/utils";

export function LocationsPage() {
  const roomTypesFilter = useRoomTypeFilter();
  const filterDefs: FilterDefs<LocationsFilter> = useMemo(
    () => ({
      type: multiFilter({
        label: "Type",
        options: safeEntries(LocationType).map(([name, id]) => ({ id, name })),
        getOptionLabel: (o) => o.name,
        getOptionValue: (o) => o.id,
        defaultValue: [LocationType.Level, LocationType.Group, LocationType.Room],
      }),
      roomTypes: roomTypesFilter,
    }),
    [roomTypesFilter],
  );

  const layoutState = useGridTableLayoutState<LocationsFilter>({
    persistedFilter: { storageKey: "adminLocationsFilter", filterDefs },
    search: "client",
  });

  const query = useAdminLocationsPageQuery({ variables: { filter: { ...layoutState.filter, version: [2] } } });
  const columns = useMemo(createColumns, []);
  const rowLookup = useRef<GridRowLookup<Row>>();
  const { openInDrawer } = useSuperDrawer();
  const rowStyles = useMemo(() => createRowStyles(openInDrawer, rowLookup), [openInDrawer, rowLookup]);

  return (
    <PageHeaderLayout pageHeader={{ title: "Locations V2" }}>
      <GridTableLayout
        hideEditColumns
        layoutState={layoutState}
        tableProps={{
          query,
          columns,
          createRows,
          as: "virtual",
          style: { allWhite: true, bordered: true },
          sorting: { on: "client" },
          rowStyles,
          rowLookup,
        }}
      />
    </PageHeaderLayout>
  );
}

type Row = SimpleHeaderAndData<AdminLocationsPage_LocationFragment>;

function createRows(data: AdminLocationsPageQuery | undefined): GridDataRow<Row>[] {
  const locations = data?.locations ?? [];
  return [simpleHeader, ...locations.map((l) => ({ kind: "data" as const, id: l.id, data: l }))];
}

function createColumns(): GridColumn<Row>[] {
  return [
    column<Row>({ header: "Name", data: ({ name }) => name, mw: "250px" }),
    column<Row>({ header: "Type", data: ({ type }) => type.name, mw: "150px" }),
    column<Row>({
      header: "Room Type",
      data: ({ roomType }) => (roomType ? chipCell([roomType.name]) : undefined),
      mw: "250px",
    }),
    column<Row>({
      header: "Path",
      data: ({ path }) => ({
        content: () => <span css={Css.wbba.truncate.$}>{path}</span>,
        tooltip: <span css={Css.wbba.$}>{path}</span>,
        value: path,
      }),
      mw: "250px",
    }),
  ];
}

function createRowStyles(
  openInDrawer: ReturnType<typeof useSuperDrawer>["openInDrawer"],
  lookup: MutableRefObject<GridRowLookup<Row> | undefined>,
): RowStyles<Row> {
  function openRow(row: GridDataRow<Row>): void {
    if (row.kind === "data") {
      const { prev, next } = lookup.current!.lookup(row)["data"];
      openInDrawer({
        onPrevClick: prev && (() => openRow(prev)),
        onNextClick: next && (() => openRow(next)),
        content: <LocationDrawer id={row.data.id} />,
        width: SuperDrawerWidth.Small,
      });
    }
  }
  return { data: { onClick: openRow } };
}
